import { Device, User } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { endOfDay } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SettingsPopup from "../logbook-edit/components/filter/SettingsPopup";
import { RouteTripsGrid } from "./components/RouteTripsGrid";
import { TripRouteMap } from "./components/TripRouteMap";
import { useRouteLogRecords } from "./hooks/useRouteLogRecords";
import { SPLIT_HANDLE_WIDTH, useSplitPane } from "./hooks/useSplitPane";
import { areSameIds } from "./utils/areSameIds";
import { type TripRange } from "./utils/mergeTripRangesByDevice";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { DriverOrDeviceSelect } from "@/components/input/DriverOrDeviceSelect";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { useDeviceById } from "@/hooks/devices/useDeviceById";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useFilteredTrips } from "@/hooks/trips/useFilteredTrips";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";
import { consumePendingDeviceId } from "@/utilities/navigation/inStructureNavigation";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

function toValidDate(value: unknown, fallback: Date) {
  const parsed = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
}

export default function RoutePage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [onlyDrivers, setOnlyDrivers] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [vehicleStatus, setVehicleStatus] = useState(false);
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>([]);
  const [colorMode, setColorMode] = useState(0);

  const { fromDate, toDate, setRange, isReady } = usePersistedDateRange({
    key: "dateRange:route",
    defaultLookbackDays: 1,
  });

  usePreselectedDevice(setDevices);

  const {
    data: trips,
    error: tripsError,
    detailsError,
    progress: tripsProgress,
    refetch: refetchTrips,
  } = useFilteredTrips({
    users,
    devices,
    toDate,
    fromDate,
    reasons: [],
    enabled: isReady,
    withAnnotations: false,
  });

  useEffect(() => {
    if (!trips?.length) {
      setSelectedTripIds(prev => (prev.length > 0 ? [] : prev));
      return;
    }

    const availableIds = new Set(trips.map(trip => trip.id));
    const validSelectedIds = selectedTripIds.filter(id => availableIds.has(id));

    if (!areSameIds(validSelectedIds, selectedTripIds)) {
      setSelectedTripIds(validSelectedIds);
    }
  }, [selectedTripIds, trips]);

  const selectedTrips = useMemo(() => {
    if (!trips?.length) {
      return [];
    }

    const selectedIds = new Set(selectedTripIds);
    return trips.filter(trip => selectedIds.has(trip.id));
  }, [selectedTripIds, trips]);

  const report = useMemo(
    () => ({
      title: t("route.title"),
      period: { from: fromDate, to: toDate },
      filter:
        [...users.map(user => getUserDisplayName(user)), ...devices.map(device => device.name)].join(", ") ||
        undefined,
    }),
    [devices, fromDate, t, toDate, users],
  );

  const fitKey = useMemo(() => [...selectedTripIds].sort().join("|"), [selectedTripIds]);

  const handleSelectedTripsChange = useCallback((nextTrips: { id: string }[]) => {
    const nextIds = nextTrips.map(trip => trip.id);
    setSelectedTripIds(prev => (areSameIds(prev, nextIds) ? prev : nextIds));
  }, []);

  const selectedTripRanges = useMemo<TripRange[]>(() => {
    return selectedTrips.map(trip => ({
      deviceId: trip.device?.id,
      start: toValidDate(trip.start, fromDate),
      stop: toValidDate(trip.stop, toDate),
    }));
  }, [fromDate, selectedTrips, toDate]);

  const selectedTripDeviceLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const trip of selectedTrips) {
      const device = trip.device as Device | undefined;
      const id = device?.id;
      if (!id || labels[id]) continue;
      labels[id] = device?.name || getDeviceLicensePlate(device) || id;
    }
    return labels;
  }, [selectedTrips]);

  const {
    records: routeLogRecords,
    statusSeries,
    isLoading: logRecordsLoading,
    isFetching: logRecordsFetching,
    error: logRecordsError,
    retry: handleLogRecordsRetry,
    statusError,
    retryStatus,
    statusLoading,
  } = useRouteLogRecords(selectedTripRanges, fromDate, toDate);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(900));
  const { containerRef, firstPaneWidth, isResizing, onResizeStart } = useSplitPane(isMobile);

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <SettingsPopup
              onlyDrivers={onlyDrivers}
              setOnlyDrivers={setOnlyDrivers}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              vehicleStatus={vehicleStatus}
              setVehicleStatus={setVehicleStatus}
            />
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <DriverOrDeviceSelect
                users={users}
                devices={devices}
                onChange={(nextUsers: User[], nextDevices: Device[]) => {
                  setUsers(nextUsers ?? []);
                  setDevices(nextDevices ?? []);
                }}
                onlyDrivers={onlyDrivers}
                showArchived={showArchived}
                showArchivedVehicles={vehicleStatus}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, mt: 0.5 }}>
                {t("route.searchActiveOnlyHint")}
              </Typography>
            </Box>
          </Box>

          <DateRangeSelect
            toDate={toDate}
            fromDate={fromDate}
            onChange={(from, to) => setRange(from, to ?? (from ? endOfDay(from) : undefined))}
          />
        </Box>
      }
      content={
        <Box
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            userSelect: isResizing ? "none" : "auto",
          }}
        >
          <Box
            sx={{
              flex: isMobile ? "1 1 auto" : "0 0 auto",
              width: isMobile
                ? "100%"
                : firstPaneWidth == null
                  ? "50%"
                  : `${Math.max(0, Math.round(firstPaneWidth))}px`,
              minHeight: isMobile ? 320 : 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <RouteTripsGrid
              trips={trips}
              error={tripsError}
              progress={tripsProgress}
              detailsError={!!detailsError}
              driverFiltered={users.length > 0}
              onRetry={() => void refetchTrips()}
              colorMode={colorMode}
              onColorModeChange={mode => setColorMode(mode)}
              onSelectedTripsChange={handleSelectedTripsChange}
              report={report}
            />
          </Box>

          {!isMobile ? (
            <Box
              onMouseDown={onResizeStart}
              sx={{
                width: `${SPLIT_HANDLE_WIDTH}px`,
                cursor: "col-resize",
                flexShrink: 0,
                position: "relative",
                bgcolor: isResizing ? "action.hover" : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  bottom: 0,
                  width: "1px",
                  transform: "translateX(-50%)",
                  bgcolor: "divider",
                }}
              />
            </Box>
          ) : null}

          <Box
            sx={{
              flex: "1 1 auto",
              borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
              minHeight: isMobile ? 320 : 0,
              minWidth: 0,
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {selectedTrips.length > 0 ? (
                <TripRouteMap
                  logRecords={routeLogRecords}
                  statusSeries={statusSeries}
                  monochrome={colorMode === 1}
                  tripRanges={selectedTripRanges}
                  deviceLabels={selectedTripDeviceLabels}
                  fitKey={fitKey}
                  loading={logRecordsLoading}
                  fetching={logRecordsFetching}
                  error={logRecordsError}
                  onRetry={handleLogRecordsRetry}
                  statusError={statusError}
                  onRetryStatus={retryStatus}
                  statusLoading={statusLoading}
                />
              ) : (
                <Box sx={{ display: "grid", placeItems: "center", width: "100%", height: "100%" }}>
                  <Typography color="text.secondary">{t("route.selectTrip")}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      }
    />
  );
}

/**
 * Starts on the vehicle handed over from the map, if one was.
 *
 * The map's route button leaves a vehicle behind and swaps this page in. Applied once: after
 * that the selection belongs to the reader, and re-applying would put the vehicle back every
 * time they cleared it.
 *
 * @param setDevices - The page's own vehicle selection.
 */
function usePreselectedDevice(setDevices: (devices: Device[]) => void) {
  const [deviceId] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("deviceId")?.trim();
    return fromUrl && fromUrl.length > 0 ? fromUrl : consumePendingDeviceId("route");
  });

  const { data: device } = useDeviceById(deviceId ?? undefined);
  const applied = useRef(false);

  useEffect(() => {
    if (device && !applied.current) {
      applied.current = true;
      setDevices([device]);
    }
  }, [device, setDevices]);
}
